/* Innate reviews — custom-rendered Google reviews (2026-07-25)
 *
 * The Google Reviews app publishes its live data as `window.grp_app_metadata`
 * (business rating, total review count, and an array of reviews with author,
 * text, rating, timestamp and profile photo). That lets us render our own
 * markup instead of restyling the app's widget, so the section is ours to
 * design.
 *
 * The app block stays on the page: it is what loads the data, and Google
 * requires attribution, which this section carries via a link to the business's
 * Google reviews. Its own widget is hidden via CSS once ours renders.
 *
 * Safe by design: if the global or the app root is missing (e.g. the app
 * changes its internals), this does nothing and the app's own widget stays
 * visible, so the page always shows reviews.
 */
(function () {
  'use strict';

  var MIN_TEXT = 70;
  var MAX_TEXT = 420;

  function stars(count) {
    var n = Math.round(count || 5);
    var out = '';
    for (var i = 0; i < 5; i++) out += i < n ? '★' : '☆';
    return out;
  }

  function formatDate(unixSeconds) {
    if (!unixSeconds) return '';
    var d = new Date(unixSeconds * 1000);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });
  }

  function pickQuotable(reviews) {
    // Prefer 5-star reviews long enough to carry the layout but not overflow it.
    var ideal = reviews.filter(function (r) {
      return r.text && r.rating >= 5 && r.text.length >= MIN_TEXT && r.text.length <= MAX_TEXT;
    });
    if (ideal.length) return ideal;

    var any = reviews.filter(function (r) {
      return r.text && r.text.length >= 30;
    });
    return any;
  }

  function build(meta, reviews) {
    var section = document.createElement('section');
    section.className = 'innate-reviews';
    section.setAttribute('aria-label', 'Customer reviews');

    var inner = document.createElement('div');
    inner.className = 'innate-reviews__inner';
    section.appendChild(inner);

    // --- Featured quote --------------------------------------------------
    var figure = document.createElement('figure');
    figure.className = 'innate-reviews__feature';

    /* Only this wrapper cross-fades. The dots sit outside it so they stay
     * steady while the quote changes. */
    var fade = document.createElement('div');
    fade.className = 'innate-reviews__fade';
    figure.appendChild(fade);

    var quoteStars = document.createElement('div');
    quoteStars.className = 'innate-reviews__stars';
    quoteStars.setAttribute('aria-hidden', 'true');
    fade.appendChild(quoteStars);

    var quote = document.createElement('blockquote');
    quote.className = 'innate-reviews__quote';
    fade.appendChild(quote);

    var caption = document.createElement('figcaption');
    caption.className = 'innate-reviews__author';
    fade.appendChild(caption);

    var dots = document.createElement('div');
    dots.className = 'innate-reviews__dots';
    figure.appendChild(dots);

    inner.appendChild(figure);

    // --- Stats column ----------------------------------------------------
    var stats = document.createElement('aside');
    stats.className = 'innate-reviews__stats';

    var score = document.createElement('div');
    score.className = 'innate-reviews__score';
    score.textContent = parseFloat(meta.rating || 5).toFixed(1);
    stats.appendChild(score);

    var statStars = document.createElement('div');
    statStars.className = 'innate-reviews__stars';
    statStars.setAttribute('aria-hidden', 'true');
    statStars.textContent = stars(parseFloat(meta.rating || 5));
    stats.appendChild(statStars);

    var count = document.createElement('p');
    count.className = 'innate-reviews__count';
    count.textContent = 'from ' + (meta.total_num_of_reviews || reviews.length) + ' Google reviews';
    stats.appendChild(count);

    // Attribution + route to the full review list on Google.
    var link = document.createElement('a');
    link.className = 'innate-reviews__link';
    link.href = meta.url || 'https://www.google.com/maps';
    link.target = '_blank';
    link.rel = 'noopener nofollow';
    link.textContent = 'Read all Google reviews →';
    stats.appendChild(link);

    inner.appendChild(stats);

    return {
      section: section,
      fade: fade,
      quote: quote,
      caption: caption,
      quoteStars: quoteStars,
      dots: dots
    };
  }

  function init() {
    var meta = window.grp_app_metadata;
    if (!meta || !Array.isArray(meta.reviews) || !meta.reviews.length) return;

    var appRoot = document.querySelector('.grp-entangle--root');
    if (!appRoot) return;

    var reviews = pickQuotable(meta.reviews);
    if (!reviews.length) return;

    var ui = build(meta, reviews);
    var index = 0;

    function render(i) {
      var r = reviews[i];
      ui.quote.textContent = '“' + r.text.trim() + '”';
      ui.quoteStars.textContent = stars(r.rating);

      var when = formatDate(r.time);
      ui.caption.textContent = r.author_name + (when ? ' · ' + when : '');

      Array.prototype.forEach.call(ui.dots.children, function (dot, di) {
        dot.classList.toggle('is-active', di === i);
        dot.setAttribute('aria-selected', di === i ? 'true' : 'false');
      });
    }

    // One dot per featured review (capped so the row stays tidy).
    var shown = Math.min(reviews.length, 5);
    reviews = reviews.slice(0, shown);
    for (var i = 0; i < shown; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'innate-reviews__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Show review ' + (i + 1));
      (function (n) {
        dot.addEventListener('click', function () {
          show(n);
          restart();
        });
      })(i);
      ui.dots.appendChild(dot);
    }

    /* Cross-fade: fade the quote out, swap the content while it is invisible,
     * then fade it back in. FADE_MS must match the CSS transition duration. */
    var FADE_MS = 320;
    var INTERVAL_MS = 6500;
    var swapping = false;

    function show(next) {
      if (swapping || next === index) return;
      swapping = true;
      ui.fade.classList.add('is-out');

      window.setTimeout(function () {
        index = next;
        render(index);
        ui.fade.classList.remove('is-out');
        swapping = false;
      }, FADE_MS);
    }

    function advance() {
      show((index + 1) % reviews.length);
    }

    var timer = null;
    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function start() {
      if (reduceMotion || reviews.length < 2 || timer) return;
      timer = window.setInterval(advance, INTERVAL_MS);
    }

    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }

    function restart() {
      stop();
      start();
    }

    render(index);

    // Pause while the visitor is reading or interacting.
    ui.section.addEventListener('mouseenter', stop);
    ui.section.addEventListener('mouseleave', start);
    ui.section.addEventListener('focusin', stop);
    ui.section.addEventListener('focusout', start);
    // Don't queue up swaps while the tab is in the background.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    start();

    // Swap the app's widget for ours only once ours is ready to display.
    appRoot.parentNode.insertBefore(ui.section, appRoot);
    document.documentElement.classList.add('innate-reviews-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
